import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  GoogleMap,
  Marker,
  DirectionsRenderer,
  DirectionsService,
  InfoWindow,
  useJsApiLoader
} from '@react-google-maps/api';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axiosInstance, { MAPKEY } from '../../../axiosInstance';
import toast from 'react-hot-toast';
import { useBlogContext } from '../../../fetchdata/BlogContext';
import mytaxi from '../../assets/img/delivery.webp';

/** ---------------- Helpers ---------------- */
const getCoord = (obj, keys = []) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && v !== '') return Number(v);
  }
  return null;
};

const extractStartEnd = (order) => {
  if (!order) return { start: null, end: null };
  const startLat = getCoord(order, ['startLat', 'pickupLat', 'fromLat', 'sourceLat', 'start_lat', 'pickup_lat']);
  const startLng = getCoord(order, ['startLng', 'pickupLng', 'fromLng', 'sourceLng', 'start_lng', 'pickup_lng']);
  const endLat   = getCoord(order, ['endLat', 'dropLat', 'toLat', 'destinationLat', 'end_lat', 'drop_lat', 'latitude']);
  const endLng   = getCoord(order, ['endLng', 'dropLng', 'toLng', 'destinationLng', 'end_lng', 'drop_lng', 'longitude']);
  const start = startLat && startLng ? { lat: startLat, lng: startLng } : null;
  const end   = endLat && endLng ? { lat: endLat, lng: endLng } : null;
  return { start, end };
};

const mapContainerStyle = { width: '100%', height: '100vh' };

// light / grayscale
const defaultOptions = {
  gestureHandling: 'greedy',
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  zoomControl: true,
  clickableIcons: false,
  styles: [
    { elementType: 'geometry', stylers: [{ saturation: -100 }, { lightness: 20 }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#4a4a4a' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#e6e6e6' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#555555' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#d9d9d9' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#8f8f8f' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#fafafa' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  ],
};

// format seconds → "1 hr 12 min" or "12 min"
const formatDuration = (sec) => {
  if (!sec && sec !== 0) return null;
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  if (h && m) return `${h} hr ${m} min`;
  if (h) return `${h} hr`;
  return `${m} min`;
};

const AccountMapOrderView = () => {
  const navigate = useNavigate();
  const { id: orderId } = useParams();

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: MAPKEY,
  });

  const [Order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const { AllLocationReq, decryptdatajson } = useBlogContext();

  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [routeRequest, setRouteRequest] = useState(null);
  const [directions, setDirections] = useState(null);
  const [currentMapLocation, setCurrentMapLocation] = useState(null);

  const [routeStats, setRouteStats] = useState({
    distanceText: null,
    durationText: null,
    durationSec: null,
    arrivalTimeText: null,
  });

  const [followMe, setFollowMe] = useState(true);
  const [userInteracting, setUserInteracting] = useState(false);
  const userInteractTimer = useRef(null);

  const [submitBusy, setSubmitBusy] = useState(false);

  // ✅ Only fit initial viewport once
  const [initialFitDone, setInitialFitDone] = useState(false);

  /** Memoized start/end (single source of truth) */
  const { start, end } = useMemo(() => extractStartEnd(Order), [Order]);

  /** Marker icons */
  const liveMarkerIcon = useMemo(() => {
    if (isLoaded && window.google?.maps) {
      return { url: mytaxi, scaledSize: new window.google.maps.Size(70, 70) };
    }
    return undefined;
  }, [isLoaded]);

  const startIcon = useMemo(() => {
    if (!isLoaded || !window.google?.maps) return undefined;
    return {
      path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
      fillColor: '#28a745',
      fillOpacity: 0.9,
      strokeWeight: 1,
      strokeColor: '#1e7e34',
      scale: 6,
    };
  }, [isLoaded]);

  const endIcon = useMemo(() => {
    if (!isLoaded || !window.google?.maps) return undefined;
    return {
      path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
      fillColor: '#dc3545',
      fillOpacity: 0.9,
      strokeWeight: 1,
      strokeColor: '#a71d2a',
      scale: 6,
    };
  }, [isLoaded]);

  /** Fetch order once */
  const getUserOrders = async () => {
    try {
      const { data } = await axiosInstance.get(`/user-orders-view/${orderId}`);
      if (!data?.success) {
        toast.error('Order not found');
        navigate('/all-order');
        return;
      }
      const raw = data.userOrder;
      const normalized = {
        ...raw,
        startLat: Number(raw?.startLat ?? raw?.pickupLat ?? raw?.fromLat ?? raw?.sourceLat ?? raw?.start_lat ?? raw?.pickup_lat ?? ''),
        startLng: Number(raw?.startLng ?? raw?.pickupLng ?? raw?.fromLng ?? raw?.sourceLng ?? raw?.start_lng ?? raw?.pickup_lng ?? ''),
        endLat:   Number(raw?.latitude ?? raw?.endLat ?? raw?.dropLat ?? raw?.toLat ?? raw?.destinationLat ?? raw?.end_lat ?? raw?.drop_lat ?? ''),
        endLng:   Number(raw?.longitude ?? raw?.endLng ?? raw?.dropLng ?? raw?.toLng ?? raw?.destinationLng ?? raw?.end_lng ?? raw?.drop_lng ?? ''),
      };
      if (Number.isNaN(normalized.startLat)) normalized.startLat = null;
      if (Number.isNaN(normalized.startLng)) normalized.startLng = null;
      if (Number.isNaN(normalized.endLat))   normalized.endLat   = null;
      if (Number.isNaN(normalized.endLng))   normalized.endLng   = null;
      setOrder(normalized);
    } catch (error) {
      console.error(error);
      toast.error('Order not found');
      navigate('/all-order');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getUserOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  /** Default current position = start (only once) */
  useEffect(() => {
    if (!currentMapLocation && start) {
      setCurrentMapLocation(start);
    }
  }, [start, currentMapLocation]);

  /** Live location from AllLocationReq (does not fit/zoom; only pans later) */
  useEffect(() => {
    if (!AllLocationReq || !AllLocationReq.length) return;
    const targetOrderId = Order?.orderId || Order?._id || orderId;

    for (let i = AllLocationReq.length - 1; i >= 0; i--) {
      const evt = AllLocationReq[i];
      if (!evt) continue;
      const matchOrder = evt.orderId === targetOrderId;
      const matchUser  = decryptdatajson?._id ? evt.userId === decryptdatajson._id : true;
      const hasCoords  = evt.currentLocation?.lat && evt.currentLocation?.lng;
      if (matchOrder && matchUser && hasCoords) {
        const lat = Number(evt.currentLocation.lat);
        const lng = Number(evt.currentLocation.lng);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          setCurrentMapLocation({ lat, lng });
        }
        break;
      }
    }
  }, [AllLocationReq, Order, decryptdatajson?._id, orderId]);

  /** Pan to live without changing zoom (no fit here) */
  useEffect(() => {
    if (!mapRef.current || !currentMapLocation) return;
    if (!followMe) return;
    if (userInteracting) return;

    const map = mapRef.current;
    const z = map.getZoom();
    map.panTo(currentMapLocation);
    // keep the user's zoom level intact
    if (typeof z === 'number') map.setZoom(z);
  }, [currentMapLocation?.lat, currentMapLocation?.lng, followMe, userInteracting]);

  const markInteracting = useCallback(() => {
    setUserInteracting(true);
    if (userInteractTimer.current) clearTimeout(userInteractTimer.current);
    userInteractTimer.current = setTimeout(() => setUserInteracting(false), 1500);
  }, []);

  /** Fit all markers (call manually or once on load) */
  const fitAll = useCallback(() => {
    try {
      if (!mapRef.current || !window.google?.maps) return;
      const map = mapRef.current;
      const bounds = new window.google.maps.LatLngBounds();
      let added = false;
      if (start) { bounds.extend(start); added = true; }
      if (end) { bounds.extend(end); added = true; }
      if (currentMapLocation) { bounds.extend(currentMapLocation); added = true; }
      if (added) map.fitBounds(bounds, { top: 100, right: 100, bottom: 100, left: 100 });
    } catch (e) { console.warn('fitAll error', e); }
  }, [start, end, currentMapLocation]);

  /** Run fit only once after map is ready */
  useEffect(() => {
    if (mapLoaded && !initialFitDone) {
      fitAll();
      setInitialFitDone(true);
    }
  }, [mapLoaded, fitAll, initialFitDone]);

  /** Build route request (prefer live → end for ETA) */
  useEffect(() => {
    if (currentMapLocation && end) {
      setRouteRequest({
        origin: currentMapLocation,
        destination: end,
        travelMode: 'DRIVING',
        drivingOptions: { departureTime: new Date(), trafficModel: 'bestguess' },
      });
      return;
    }
    if (start && end) {
      setRouteRequest({
        origin: start,
        destination: end,
        travelMode: 'DRIVING',
        drivingOptions: { departureTime: new Date(), trafficModel: 'bestguess' },
      });
      return;
    }
    setRouteRequest(null);
    setDirections(null);
    setRouteStats({ distanceText: null, durationText: null, durationSec: null, arrivalTimeText: null });
  }, [start, end, currentMapLocation]);

  /** Save (unchanged) */
  const UserUpdateData = async () => {
    if (!Order) return;
    setSubmitBusy(true);
    try {
      const res = await axiosInstance.put(`/admin/update-full-order/${orderId}`, Order);
      if (res?.data?.success) { toast.success('Data updated successfully!'); getUserOrders(); }
      else { toast.error('Update failed.'); }
    } catch (error) { console.error('Update error', error); toast.error('Error updating order'); }
    finally { setSubmitBusy(false); }
  };

  const openInGoogleMaps = () => {
    if (currentMapLocation && end) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&origin=${currentMapLocation.lat},${currentMapLocation.lng}&destination=${end.lat},${end.lng}&travelmode=driving`,
        '_blank'
      );
    } else if (start && end) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&origin=${start.lat},${start.lng}&destination=${end.lat},${end.lng}&travelmode=driving`,
        '_blank'
      );
    } else {
      toast.error('Missing start/end coordinates.');
    }
  };

  /** Guards */
  if (loadError) return <div className="alert alert-danger m-3">Maps failed to load. Check API key & restrictions.</div>;
  if (!isLoaded)   return <div className="m-3">Loading…</div>;

  return (
    <>
      {/* Follow / Fit controls */}
      <div className="position-absolute bottom-0 start-0 m-3" style={{ zIndex: 20 }}>
        <div className="btn-group">
          <button
            className={`btn btn-sm ${followMe ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFollowMe((v) => !v)}
            title="Toggle follow me"
          >
            {followMe ? 'Following You' : 'Follow Me'}
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={fitAll} title="Fit to markers">
            Fit
          </button>
        </div>
      </div>

      {/* Top overlay UI */}
      <div className="position-absolute w-100" style={{ zIndex: 10, pointerEvents: 'none' }}>
        <div className="container pt-3">
          <div className="row g-3">
            <div className="col-12 col-lg-5" style={{ pointerEvents: 'auto' }}>
              <div className="card shadow-sm border rounded-4">
                <div className="card-body p-md-2 p-2">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="small text-muted ">
                      Order ID:&nbsp;
                      <span className="text-dark fw-semibold">{Order?.orderId || Order?._id || '-'}</span>
                    </div>
                    <span className={`badge ${isLoading ? 'bg-secondary' : 'bg-success'}`}>
                      {isLoading ? 'Loading' : 'Live'}
                    </span>
                  </div>

                  <div className="row g-2">
                    <div className="col-12">
                      <div className="p-2 rounded-3 bg-light">
                        <div className="d-flex align-items-center">
                          <div className="me-2 rounded-circle" style={{ width: 10, height: 10, background: '#28a745' }} />
                          <div className="d-flex gap-2 align-items-center">
                            <div className="fw-semibold">Start</div>
                            <div className="small text-muted">
                              {start ? `${start.lat.toFixed(5)}, ${start.lng.toFixed(5)}` : '—'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="p-2 rounded-3 bg-light">
                        <div className="d-flex align-items-center">
                          <div className="me-2 rounded-circle" style={{ width: 10, height: 10, background: '#dc3545' }} />
                          <div className="d-flex gap-2 align-items-center">
                            <div className="fw-semibold">End</div>
                            <div className="small text-muted">
                              {end ? `${end.lat.toFixed(5)}, ${end.lng.toFixed(5)}` : '—'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ETA / Distance */}
                    {routeStats.durationText && (
                      <div className="col-12">
                        <div className="d-flex flex-wrap gap-2">
                          <span className="badge text-bg-dark">ETA: {routeStats.durationText}</span>
                          {routeStats.arrivalTimeText && (
                            <span className="badge text-bg-dark">Arrival: {routeStats.arrivalTimeText}</span>
                          )}
                          {routeStats.distanceText && (
                            <span className="badge text-bg-dark">{routeStats.distanceText}</span>
                          )}
                        </div>
                        <div className="small text-muted mt-1">
                          from {currentMapLocation ? 'driver location' : 'start point'} (traffic: best guess)
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="d-flex gap-2 mt-3">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={openInGoogleMaps}
                      disabled={!end && !currentMapLocation && !start}
                    >
                      Open in Google Maps
                    </button>

                    <button type="button" className="btn btn-secondary btn-sm" onClick={fitAll} title="Fit to markers">
                      Fit
                    </button>

                    <button type="button" className="btn btn-success btn-sm ms-auto" onClick={UserUpdateData} disabled={submitBusy}>
                      {submitBusy ? 'Saving…' : 'Save Order'}
                    </button>
                  </div>

                </div>
              </div>
            </div>

          </div>
        </div>
      </div>


{!currentMapLocation  ? 
<div className='d-flex align-items-center justify-content-center' style={{minHeight:'100vh'}}>
  <div className='text-center px-3'>
<img src="/assets/img/driver.webp" width={200}/>
<h2 className='text-danger mt-2'> Driver is not assign yet!</h2>
<Link className="btn btn-dark  mt-4 d-inline-block px-4" to="/account/all-order">
<i className="ri-arrow-left-line"/> Go Back </Link>
  </div>
</div>

:
 
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        defaultCenter={start || { lat: 28.6139, lng: 77.2090 }}
        zoom={14}
        options={defaultOptions}
        onLoad={(map) => { mapRef.current = map; setMapLoaded(true); }}
        onDragStart={markInteracting}
        onDragEnd={markInteracting}
        onZoomChanged={markInteracting}
      >
        {/* Start Marker */}
        {start && (
          <Marker
            position={start}
            icon={startIcon}
            onClick={() => setSelectedMarker({ type: 'start', position: start })}
          />
        )}

        {/* End Marker */}
        {end && (
          <Marker
            position={end}
            icon={endIcon}
            onClick={() => setSelectedMarker({ type: 'end', position: end })}
          />
        )}

        {/* Live Marker */}
        {currentMapLocation && (
          <Marker
            position={currentMapLocation}
            icon={liveMarkerIcon}
            onClick={() => setSelectedMarker({ type: 'live', position: currentMapLocation })}
          />
        )}

        {/* InfoWindow */}
        {selectedMarker && (
          <InfoWindow
            position={selectedMarker.position}
            onCloseClick={() => setSelectedMarker(null)}
          >
            <div className="p-1">
              <div className="fw-semibold mb-1">
                {selectedMarker.type === 'start' && 'Start Location'}
                {selectedMarker.type === 'end' && 'End Location'}
                {selectedMarker.type === 'live' && 'Driver Live Location'}
              </div>
              <div className="small text-muted">
                {selectedMarker.position.lat.toFixed(6)}, {selectedMarker.position.lng.toFixed(6)}
              </div>
            </div>
          </InfoWindow>
        )}

        {/* Directions + parse ETA (preserveViewport so zoom stays) */}
        {routeRequest && (
          <DirectionsService
            options={routeRequest}
            callback={(res, status) => {
              if (status === 'OK') {
                setDirections(res);

                const legs = res?.routes?.[0]?.legs || [];
                let totalSec = 0;
                let totalMeters = 0;

                legs.forEach((leg) => {
                  const sec = (leg.duration_in_traffic?.value ?? leg.duration?.value) || 0;
                  const m   = leg.distance?.value || 0;
                  totalSec += sec;
                  totalMeters += m;
                });

                const distanceText =
                  legs.length === 1
                    ? legs[0]?.distance?.text || null
                    : `${(totalMeters / 1000).toFixed(1)} km`;

                const durationText =
                  legs.length === 1
                    ? (legs[0]?.duration_in_traffic?.text || legs[0]?.duration?.text || null)
                    : formatDuration(totalSec);

                const arrival = new Date(Date.now() + totalSec * 1000);
                const arrivalTimeText = arrival.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                setRouteStats({
                  distanceText,
                  durationText,
                  durationSec: totalSec,
                  arrivalTimeText,
                });
              } else {
                setDirections(null);
                setRouteStats({ distanceText: null, durationText: null, durationSec: null, arrivalTimeText: null });
              }
            }}
          />
        )}
        {directions && (
          <DirectionsRenderer
            options={{
              directions,
              preserveViewport: true,   // ← keep user zoom/pan
              suppressMarkers: true,
            }}
          />
        )}
      </GoogleMap>
}
      
    </>
  );
};

export default AccountMapOrderView;
